import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

import { aiUsageService } from "../services/aiUsageService";
import { creditsService } from "../services/creditsService";
import { light as C, MONO, radius, injectKeyframes, rise } from "../../hiring/theme";

// Where the AI credits go.
//
// WHAT THIS PAGE IS FOR
// Not reassurance. A dashboard that only says "you have spent ₹188" is a number
// nobody can act on. Every panel here is built to support one decision:
//
//   Which step costs the money?      → the pipeline breakdown
//   Is that a lot?                   → cost per interview, not total spend
//   Is it getting worse?             → the daily columns
//   Is any of it waste?              → failures, which are billed
//   Is the caching earning its keep? → the voice panel
//
// THE MONEY IS SMALL AND THAT IS THE POINT
// Figures here run to fractions of a rupee, and rounding them to two decimals
// would show most of this page as ₹0.00. So the formatter below keeps
// significant digits rather than a fixed scale — an interview costing ₹1.87 is
// the headline finding of this whole module, and "₹2" throws away the part that
// makes it credible.

// ── Colour ──────────────────────────────────────────────────────────────────
//
// Not a categorical rainbow. Seven unrelated hues would say these are seven
// unrelated things, when they are seven parts of one bill — and the module's
// existing rule is three meanings, three colours, nothing coloured for being
// pretty.
//
// So the ramp encodes what KIND of work was paid for:
//
//   teal    — reading. Taking something in: a resume, a recorded answer.
//   slate   — thinking. Producing judgement: questions, follow-ups, a verdict.
//   amber   — speaking. Maya's voice, which is amber everywhere in this module.
//
// Shade within a family separates the steps. The result reads as one system
// with three kinds of work in it, which is what it is.
const STEP_COLOR = {
  RESUME_PARSE: "#0f766e",
  RESUME_RANK: "#5eaaa3",
  TRANSCRIBE: "#8ec9c1",

  QUESTION_GEN: "#3f4a6b",
  DEEPEN_GEN: "#6b7699",
  INTERVIEW_SCORE: "#9aa2bd",

  TTS: "#b45309",
};
const FALLBACK_COLOR = "#a8a29e";

// ── Money ───────────────────────────────────────────────────────────────────
//
// The page reads in rupees because that is what the person looking at it
// budgets in. The API still speaks USD throughout — the provider bills in
// dollars and the ledger stores dollars, so converting at read time is the only
// place a rate can change later without rewriting history.
//
// The rate arrives from the server (HIRING_INR_PER_USD) rather than living here,
// so correcting a stale rate is a restart and not a frontend rebuild. The 88
// below is only the floor for the first paint, before the response lands.
//
// Set through setRate() rather than threaded through every component as a prop.
// A module-level value is the wrong instinct nine times in ten, but the
// alternative here is passing a scalar through six layers of presentational
// components that have no other reason to know about currency — and there is
// exactly one dashboard on screen at a time.
let INR_PER_USD = 88;
export function setRate(r) {
  if (Number(r) > 0) INR_PER_USD = Number(r);
}

/**
 * Rupees, keeping resolution at every scale.
 *
 * A fixed two decimals would render most of this page as ₹0.00 — a single
 * transcription costs about ₹0.016 — and the small numbers are the whole point.
 * An interview at ₹1.87 is the headline finding of this module; rounding it to
 * ₹2 throws away the part that makes it credible.
 */
function money(usd) {
  const n = Number(usd || 0) * INR_PER_USD;
  if (n === 0) return "₹0";
  if (n >= 1000) return `₹${Math.round(n).toLocaleString("en-IN")}`;
  if (n >= 100) return `₹${n.toFixed(0)}`;
  if (n >= 10) return `₹${n.toFixed(1)}`;
  if (n >= 1) return `₹${n.toFixed(2)}`;
  if (n >= 0.01) return `₹${n.toFixed(3)}`;
  if (n >= 0.0001) return `₹${n.toFixed(4)}`;
  return "<₹0.0001";
}

/**
 * The same figure in the currency it was actually billed in.
 *
 * Used sparingly and on purpose: beside the headline total, and beside the
 * monthly cap — which is stored and edited in USD on the Settings screen, so
 * showing only rupees there would send someone to type a rupee figure into a
 * dollar field.
 */
function dollars(usd) {
  const n = Number(usd || 0);
  if (n === 0) return "$0";
  if (n >= 100) return `$${n.toFixed(0)}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(3)}`;
  if (n >= 0.0001) return `$${n.toFixed(5)}`;
  return "<$0.0001";
}

const compact = (n) => Number(n || 0).toLocaleString("en-IN");

function duration(ms) {
  const n = Number(ms || 0);
  if (!n) return "—";
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${Math.round(n)}ms`;
}

// ── Small parts ─────────────────────────────────────────────────────────────

const Label = ({ children, sx }) => (
  <Typography
    sx={{
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: ".1em",
      textTransform: "uppercase",
      color: C.faint,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const Card = ({ children, sx, ...rest }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2.5, md: 3 },
      borderRadius: radius.lg,
      border: `1px solid ${C.hair}`,
      bgcolor: C.card,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Paper>
);

/**
 * One headline number.
 *
 * `hint` sits under the value and is where the honesty lives — a projection is
 * labelled as a run rate, a cost per interview names how many interviews it was
 * divided by. A number with no denominator on a spend page is how people talk
 * themselves into the wrong conclusion.
 */
const Stat = ({ label, value, hint, accent = C.ink, tone, index = 0 }) => (
  <Card sx={{ flex: "1 1 200px", minWidth: 0, ...rise(index) }}>
    <Label>{label}</Label>
    <Typography
      sx={{
        mt: 1.25,
        fontSize: { xs: 26, md: 30 },
        fontWeight: 700,
        letterSpacing: "-.02em",
        lineHeight: 1.1,
        color: accent,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ mt: 0.75, fontSize: 12.5, lineHeight: 1.5, color: tone || C.muted }}>
        {hint}
      </Typography>
    )}
  </Card>
);

/** The whole bill as one bar. Answers "what is the big slice" before any reading. */
const ShareBar = ({ steps }) => {
  const total = steps.reduce((t, s) => t + s.cost_usd, 0);
  if (!total) return null;
  return (
    <Box
      sx={{
        display: "flex",
        height: 12,
        borderRadius: 99,
        overflow: "hidden",
        bgcolor: C.panel,
      }}
    >
      {steps.map((s) => (
        <Tooltip
          key={s.key}
          arrow
          title={`${s.label} — ${money(s.cost_usd)} (${s.share}%)`}
        >
          <Box
            sx={{
              width: `${(s.cost_usd / total) * 100}%`,
              bgcolor: STEP_COLOR[s.key] || FALLBACK_COLOR,
              transition: "opacity .18s",
              "&:hover": { opacity: 0.75 },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

/**
 * Spend per day, stacked by step.
 *
 * Hand-built rather than pulled from the charting library already in the
 * bundle. The data is nine rows and the chart is thirty divs; a ResponsiveContainer
 * around it would add a render cycle, a resize observer and a second set of
 * colour defaults to override, for a picture that is this many lines of CSS.
 *
 * Gaps are filled here and not in SQL: a day with no hiring activity produces no
 * row server-side, and only the browser knows which timezone's "day" the person
 * reading this lives in.
 */
const DailyChart = ({ daily, days }) => {
  const { columns, max } = useMemo(() => {
    const byDay = new Map();
    for (const r of daily) {
      if (!byDay.has(r.day)) byDay.set(r.day, { day: r.day, total: 0, parts: [] });
      const d = byDay.get(r.day);
      d.total += r.cost_usd;
      d.parts.push(r);
    }

    // A dense axis, so a quiet week reads as a quiet week rather than as three
    // bars sitting next to each other implying continuous activity.
    const span = Math.min(days, 60);
    const out = [];
    const today = new Date();
    for (let i = span - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate(),
      ).padStart(2, "0")}`;
      out.push(byDay.get(key) || { day: key, total: 0, parts: [] });
    }
    return { columns: out, max: Math.max(...out.map((c) => c.total), 0.000001) };
  }, [daily, days]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          height: 140,
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        {columns.map((col) => (
          <Tooltip
            key={col.day}
            arrow
            title={
              col.total ? (
                <Box>
                  <Box sx={{ fontWeight: 700, mb: 0.5 }}>
                    {col.day} — {money(col.total)}
                  </Box>
                  {col.parts
                    .slice()
                    .sort((a, b) => b.cost_usd - a.cost_usd)
                    .map((p) => (
                      <Box key={p.key} sx={{ fontSize: 11.5 }}>
                        {p.key} · {money(p.cost_usd)} · {p.calls} calls
                      </Box>
                    ))}
                </Box>
              ) : (
                `${col.day} — nothing spent`
              )
            }
          >
            <Box
              sx={{
                flex: "1 1 0",
                minWidth: 6,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                cursor: "default",
              }}
            >
              {col.total ? (
                col.parts
                  .slice()
                  .sort((a, b) => b.cost_usd - a.cost_usd)
                  .map((p, i) => (
                    <Box
                      key={p.key}
                      sx={{
                        height: `${(p.cost_usd / max) * 100}%`,
                        bgcolor: STEP_COLOR[p.key] || FALLBACK_COLOR,
                        borderRadius: i === 0 ? "3px 3px 0 0" : 0,
                        minHeight: 2,
                      }}
                    />
                  ))
              ) : (
                // A visible floor rather than nothing. An empty column is data —
                // it says the pipeline was idle — and a blank gap reads as
                // missing data instead.
                <Box sx={{ height: 2, bgcolor: C.line, borderRadius: 99 }} />
              )}
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Label sx={{ fontSize: 10 }}>{columns[0]?.day}</Label>
        <Label sx={{ fontSize: 10 }}>{columns[columns.length - 1]?.day}</Label>
      </Stack>
    </Box>
  );
};

/** One pipeline step, with its own bar so relative size is readable without maths. */
const StepRow = ({ step, max, index }) => {
  const color = STEP_COLOR[step.key] || FALLBACK_COLOR;
  return (
    <Box
      sx={{
        py: 2,
        borderTop: index === 0 ? "none" : `1px solid ${C.hair}`,
        ...rise(index),
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.75 }}>
        <Box
          sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: color, flexShrink: 0 }}
        />
        <Typography sx={{ fontWeight: 650, fontSize: 14.5, color: C.ink }}>
          {step.label}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: C.faint }}>{step.when}</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 15,
            color: C.ink,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {money(step.cost_usd)}
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            color: C.muted,
            width: 46,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {step.share}%
        </Typography>
      </Stack>

      <Box sx={{ pl: 3 }}>
        <Box
          sx={{
            height: 6,
            borderRadius: 99,
            bgcolor: C.panel,
            overflow: "hidden",
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: `${max ? (step.cost_usd / max) * 100 : 0}%`,
              height: "100%",
              bgcolor: color,
              borderRadius: 99,
            }}
          />
        </Box>

        <Typography sx={{ fontSize: 13, color: C.body, mb: 0.75 }}>
          {step.detail}
        </Typography>

        <Stack direction="row" flexWrap="wrap" sx={{ gap: 2, rowGap: 0.5 }}>
          <Meta k="Calls" v={compact(step.calls)} />
          <Meta k="Each" v={money(step.avg_cost_usd)} />
          <Meta k="Latency" v={duration(step.avg_latency_ms)} />
          {step.input_tokens + step.output_tokens > 0 && (
            <Meta
              k="Tokens"
              v={`${compact(step.input_tokens)} in · ${compact(step.output_tokens)} out`}
            />
          )}
          {step.errors > 0 && (
            <Meta k="Failed" v={compact(step.errors)} tone={C.negative} />
          )}
          {step.unpriced > 0 && (
            <Meta k="Unpriced" v={compact(step.unpriced)} tone={C.caution} />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

const Meta = ({ k, v, tone }) => (
  <Stack direction="row" spacing={0.75} alignItems="baseline">
    <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: C.faint, letterSpacing: ".06em" }}>
      {k.toUpperCase()}
    </Typography>
    <Typography
      sx={{
        fontSize: 12.5,
        fontWeight: 600,
        color: tone || C.body,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {v}
    </Typography>
  </Stack>
);

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

// ── Page ────────────────────────────────────────────────────────────────────

export default function AiUsage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // A store's own allowance. Separate from the spend report because a store can
  // be out of credits with a perfectly healthy usage history — the two answer
  // different questions and one of them stops work.
  const [wallet, setWallet] = useState(null);

  useEffect(() => injectKeyframes(), []);

  const load = useCallback(async (span) => {
    setLoading(true);
    setError("");
    try {
      // Fetched alongside, not before: a failure to read the balance must not
      // hide the spend report, which is the rest of this page.
      creditsService
        .mine()
        .then((r) => setWallet(r?.data?.data || null))
        .catch(() => setWallet(null));

      const res = await aiUsageService.get(span);
      const payload = res?.data?.data || res?.data || null;
      // Before setData, so the first render already uses the server's rate
      // rather than painting the 88 default and correcting itself a frame later.
      setRate(payload?.currency?.inr_per_usd);
      setData(payload);
    } catch (err) {
      setError(
        err?.response?.data?.statusMessage ||
          err?.message ||
          "Could not load AI usage.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const steps = data?.steps || [];
  const maxStep = Math.max(...steps.map((s) => s.cost_usd), 0);
  const totalErrors = steps.reduce((t, s) => t + s.errors, 0);

  // The window is short at first — a tenant two days in has two days of data —
  // so anything multiplied out to a month says so rather than implying a month
  // was observed.
  const thin = (data?.window?.observed_days || 0) < days * 0.8;

  if (loading && !data) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: 420 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={26} sx={{ color: C.act }} />
          <Label>Reading the ledger</Label>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto", pb: 8, fontFamily: "inherit" }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "flex-end" }}
        spacing={2}
        sx={{ mb: 3.5 }}
      >
        <Box sx={{ flex: 1 }}>
          <Label>Hiring AI</Label>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: { xs: 24, md: 30 },
              fontWeight: 700,
              letterSpacing: "-.02em",
              color: C.ink,
            }}
          >
            Where the credits go
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13.5, color: C.muted }}>
            Every model call this module makes, what it was for, and what it cost.
            {data?.window?.observed_days ? (
              <> {data.window.observed_days} days of history so far.</>
            ) : null}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {RANGES.map((r) => (
            <Button
              key={r.days}
              size="small"
              onClick={() => setDays(r.days)}
              sx={{
                textTransform: "none",
                fontWeight: 650,
                fontSize: 13,
                borderRadius: radius.pill,
                px: 1.75,
                color: days === r.days ? C.card : C.muted,
                bgcolor: days === r.days ? C.act : "transparent",
                "&:hover": {
                  bgcolor: days === r.days ? C.act : C.panel,
                },
              }}
            >
              {r.label}
            </Button>
          ))}
          <Tooltip title="Reload">
            <span>
              <Button
                size="small"
                onClick={() => load(days)}
                disabled={loading}
                sx={{ minWidth: 0, px: 1, color: C.muted }}
              >
                <RefreshRoundedIcon sx={{ fontSize: 19 }} />
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 99, height: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: radius.md }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          {/* ── Allowance ────────────────────────────────────────────────── */}
          {/*
            First, above the spend. Somebody whose invitations are being refused
            opens this page for one reason, and making them read a month of cost
            analysis to find it would be the wrong order.

            Hidden entirely for an exempt store: Kumudu and BYOK tenants have no
            allowance, and a balance of zero shown beside them reads as "out of
            credits" rather than "not applicable".
          */}
          {wallet && !wallet.exempt && (
            <Card
              sx={{
                mb: 2,
                borderColor:
                  wallet.balance <= 0
                    ? C.negativeSoft
                    : wallet.interviews_left <= 2
                      ? C.cautionSoft
                      : C.hair,
                bgcolor:
                  wallet.balance <= 0
                    ? C.negativeSoft
                    : wallet.interviews_left <= 2
                      ? C.cautionSoft
                      : C.card,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.5, sm: 3 }}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Label>AI credits</Label>
                  <Typography
                    sx={{
                      mt: 0.75,
                      fontSize: 26,
                      fontWeight: 700,
                      lineHeight: 1.1,
                      color: wallet.balance <= 0 ? C.negative : C.ink,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {compact(wallet.balance)}
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: C.body, lineHeight: 1.55 }}>
                    {wallet.balance <= 0 ? (
                      <>
                        You are out of credits. Interviews and resume screening
                        will not run until Kumudu tops you up.
                      </>
                    ) : (
                      <>
                        About{" "}
                        <b>
                          {wallet.interviews_left} more interview
                          {wallet.interviews_left === 1 ? "" : "s"}
                        </b>
                        , or {Math.floor(wallet.balance / (wallet.prices?.RESUME_SCREEN || 2))}{" "}
                        resume screenings.
                      </>
                    )}
                  </Typography>
                </Box>
                {wallet.spent_30d > 0 && (
                  <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                    <Label>Used · 30 days</Label>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 18,
                        fontWeight: 700,
                        color: C.body,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {compact(wallet.spent_30d)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Card>
          )}

          {/* ── The four numbers ─────────────────────────────────────────── */}
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 2, mb: 2 }}>
            <Stat
              index={0}
              label={`Spent · last ${days} days`}
              value={money(data.totals.cost_usd)}
              hint={`${dollars(data.totals.cost_usd)} billed · ${compact(
                data.totals.calls,
              )} model calls`}
            />
            <Stat
              index={1}
              label="Cost per interview"
              accent={C.act}
              value={
                data.unit_economics.cost_per_interview_usd
                  ? money(data.unit_economics.cost_per_interview_usd)
                  : "—"
              }
              hint={
                data.unit_economics.interviews_completed
                  ? `across ${data.unit_economics.interviews_completed} completed interview${
                      data.unit_economics.interviews_completed === 1 ? "" : "s"
                    }`
                  : "No interviews completed in this window"
              }
            />
            <Stat
              index={2}
              label="Run rate"
              value={`${money(data.totals.per_day_usd)}/day`}
              hint={
                thin
                  ? `≈ ${money(
                      data.totals.projected_month_usd,
                    )}/month at this rate — based on only ${
                      data.window.observed_days
                    } days, so treat it loosely`
                  : `≈ ${money(data.totals.projected_month_usd)} over a month`
              }
              tone={thin ? C.caution : C.muted}
            />
            <Stat
              index={3}
              label="Monthly budget"
              value={
                data.budget.uncapped
                  ? "No limit"
                  : `${data.budget.percent_used}%`
              }
              accent={data.budget.over_cap ? C.negative : C.ink}
              hint={
                data.budget.uncapped
                  ? `${money(
                      data.budget.spent_usd,
                    )} spent this month · set a cap in Settings`
                  : `${money(data.budget.spent_usd)} of ${money(
                      data.budget.cap_usd,
                    )} · cap is set in USD (${dollars(data.budget.cap_usd)})`
              }
              tone={data.budget.over_cap ? C.negative : C.muted}
            />
          </Stack>

          {/* ── The reconciliation warning ───────────────────────────────── */}
          {data.voice.untracked_clips > 0 && (
            <Alert
              severity="warning"
              icon={<InfoOutlinedIcon />}
              sx={{
                mb: 2,
                borderRadius: radius.md,
                border: `1px solid ${C.cautionSoft}`,
                bgcolor: C.cautionSoft,
                color: C.body,
                "& .MuiAlert-icon": { color: C.caution },
              }}
            >
              <Typography sx={{ fontSize: 13.5, fontWeight: 650, color: C.ink }}>
                {money(data.voice.untracked_usd)} of speech is missing from the
                figures above
              </Typography>
              <Typography sx={{ fontSize: 13, mt: 0.5, lineHeight: 1.6 }}>
                {data.voice.untracked_clips} voice clips were generated before
                speech was added to the audit ledger, so they were paid for but
                never recorded against a step. Clips generated from now on appear
                under <b>Maya's voice</b>. This notice disappears on its own as
                the older clips fall outside the window.
              </Typography>
            </Alert>
          )}

          {/* ── Where it goes ────────────────────────────────────────────── */}
          <Card sx={{ mb: 2 }}>
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Label>The pipeline</Label>
                <Typography sx={{ mt: 0.5, fontWeight: 700, fontSize: 17, color: C.ink }}>
                  What the money bought
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: C.faint }}>
                teal = reading · slate = thinking · amber = speaking
              </Typography>
            </Stack>

            <ShareBar steps={steps} />

            <Box sx={{ mt: 1 }}>
              {steps.length === 0 && (
                <Typography sx={{ py: 4, textAlign: "center", fontSize: 13.5, color: C.muted }}>
                  No model calls in this window.
                </Typography>
              )}
              {steps.map((s, i) => (
                <StepRow key={s.key} step={s} max={maxStep} index={i} />
              ))}
            </Box>
          </Card>

          {/* ── Trend ────────────────────────────────────────────────────── */}
          <Card sx={{ mb: 2 }}>
            <Label>Daily</Label>
            <Typography sx={{ mt: 0.5, mb: 2.5, fontWeight: 700, fontSize: 17, color: C.ink }}>
              Spend over time
            </Typography>
            <DailyChart daily={data.daily} days={days} />
          </Card>

          {/* ── Voice + models, side by side ─────────────────────────────── */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            <Card sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BoltRoundedIcon sx={{ fontSize: 18, color: C.maya }} />
                <Label>Voice cache</Label>
              </Stack>
              <Typography sx={{ mt: 0.5, mb: 0.5, fontWeight: 700, fontSize: 17, color: C.ink }}>
                {money(data.voice.avoided_usd)} not spent
              </Typography>
              <Typography sx={{ fontSize: 13, color: C.body, lineHeight: 1.65, mb: 2 }}>
                Most of an interview is the same for every candidate on a job — the
                introduction, the screening questions, the closing. Those clips are
                generated once and reused, so {compact(data.voice.hits)} playbacks
                cost {money(data.voice.spent_usd)} instead of{" "}
                {money(data.voice.spent_usd + data.voice.avoided_usd)}.
              </Typography>
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 2.5, rowGap: 1 }}>
                <Meta k="Clips" v={compact(data.voice.clips)} />
                <Meta k="Playbacks" v={compact(data.voice.hits)} />
                <Meta k="Audio" v={`${data.voice.minutes} min`} />
                <Meta k="Stored" v={`${data.voice.megabytes} MB`} />
              </Stack>
              {data.voice.shared_across_tenants && (
                <Typography sx={{ mt: 2, fontSize: 12, color: C.faint, lineHeight: 1.55 }}>
                  Clips are keyed by their text, so identical questions are shared
                  rather than duplicated. On a multi-tenant install these totals
                  cover the whole platform, not one organisation.
                </Typography>
              )}
            </Card>

            <Card sx={{ flex: 1, minWidth: 0 }}>
              <Label>Models</Label>
              <Typography sx={{ mt: 0.5, mb: 2, fontWeight: 700, fontSize: 17, color: C.ink }}>
                Who answered
              </Typography>
              {data.models.length === 0 && (
                <Typography sx={{ fontSize: 13.5, color: C.muted }}>Nothing yet.</Typography>
              )}
              {data.models.map((m, i) => (
                <Box
                  key={m.model}
                  sx={{
                    py: 1.5,
                    borderTop: i === 0 ? "none" : `1px solid ${C.hair}`,
                  }}
                >
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography
                      sx={{
                        fontFamily: MONO,
                        fontSize: 12.5,
                        fontWeight: 500,
                        color: C.ink,
                        wordBreak: "break-all",
                      }}
                    >
                      {m.model}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: C.ink,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {money(m.cost_usd)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" sx={{ gap: 2, rowGap: 0.5, mt: 0.5 }}>
                    <Meta k="Calls" v={compact(m.calls)} />
                    <Meta k="Latency" v={duration(m.avg_latency_ms)} />
                    {m.errors > 0 && <Meta k="Failed" v={compact(m.errors)} tone={C.negative} />}
                  </Stack>
                </Box>
              ))}
            </Card>
          </Stack>

          {/* ── By job ───────────────────────────────────────────────────── */}
          {data.jobs.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <Label>By role</Label>
              <Typography sx={{ mt: 0.5, mb: 2, fontWeight: 700, fontSize: 17, color: C.ink }}>
                What each opening cost
              </Typography>
              {data.jobs.map((j, i) => (
                <Stack
                  key={`${j.job_id}-${i}`}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    py: 1.5,
                    borderTop: i === 0 ? "none" : `1px solid ${C.hair}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 650,
                        fontSize: 14,
                        color: j.job_id ? C.ink : C.muted,
                        fontStyle: j.job_id ? "normal" : "italic",
                      }}
                      noWrap
                    >
                      {j.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: C.faint }}>
                      {compact(j.calls)} calls
                      {j.candidates ? ` · ${compact(j.candidates)} candidates` : ""}
                    </Typography>
                  </Box>
                  {j.candidates > 0 && (
                    <Chip
                      size="small"
                      label={`${money(j.cost_usd / j.candidates)} each`}
                      sx={{
                        bgcolor: C.actSoft,
                        color: C.act,
                        fontWeight: 650,
                        fontSize: 11.5,
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: C.ink,
                      fontVariantNumeric: "tabular-nums",
                      minWidth: 70,
                      textAlign: "right",
                    }}
                  >
                    {money(j.cost_usd)}
                  </Typography>
                </Stack>
              ))}
            </Card>
          )}

          {/* ── What the rupee figures rest on ───────────────────────────── */}
          <Typography
            sx={{
              mt: 3,
              mb: 2,
              fontSize: 12,
              color: C.faint,
              lineHeight: 1.6,
            }}
          >
            Amounts are shown in rupees at{" "}
            <b>₹{data.currency?.inr_per_usd ?? 88} to the dollar</b>
            {data.currency?.configured ? "" : " (the built-in default)"}. Your
            provider bills in USD, so the dollar figures are the ones that
            reconcile against an invoice.
            {data.currency?.configured
              ? ""
              : " Set HIRING_INR_PER_USD on the server to use a current rate."}
          </Typography>

          {/* ── Waste ────────────────────────────────────────────────────── */}
          {data.failures.length > 0 && (
            <Card>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: C.negative }} />
                <Label>Failed calls</Label>
              </Stack>
              <Typography sx={{ mt: 0.5, fontWeight: 700, fontSize: 17, color: C.ink }}>
                {compact(totalErrors)} calls did not produce a result
              </Typography>
              <Typography sx={{ mt: 0.5, mb: 2, fontSize: 13, color: C.body, lineHeight: 1.6 }}>
                A call that fails after the model has read the prompt is still
                billed. This is usually where an unexplained bill comes from.
              </Typography>
              {data.failures.map((f, i) => (
                <Box
                  key={i}
                  sx={{
                    py: 1.5,
                    borderTop: i === 0 ? `1px solid ${C.hair}` : `1px solid ${C.hair}`,
                  }}
                >
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={f.label}
                      sx={{
                        bgcolor: C.negativeSoft,
                        color: C.negative,
                        fontWeight: 650,
                        fontSize: 11,
                        height: 20,
                      }}
                    />
                    <Typography sx={{ fontSize: 12.5, color: C.faint }}>
                      ×{compact(f.occurrences)} · last {new Date(f.last_seen).toLocaleString()}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 650, color: C.ink }}>
                      {money(f.cost_usd)}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 12,
                      color: C.body,
                      lineHeight: 1.6,
                      bgcolor: C.panel,
                      p: 1.25,
                      borderRadius: radius.sm,
                      overflowX: "auto",
                    }}
                  >
                    {f.error}
                  </Typography>
                </Box>
              ))}
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
